import { useEffect, useRef, useState } from 'react';
import { useAppApi } from '@/lib/api';
import { MODEL_OPTIONS } from '@/lib/modelCatalog';

/**
 * The model list for one (provider, connection) pair, from GET /api/agent/models — the
 * app_provider_models catalogue the server refreshes off the provider's own /models
 * endpoint. Replaces reading the hardcoded MODEL_OPTIONS array directly, which went
 * stale every time a provider shipped something.
 *
 * Its own module rather than part of lib/modelCatalog.js on purpose: that file is
 * imported by lib/workflowPalette.js, which is pure data and must not pull React into
 * its bundle.
 *
 * Never throws and never leaves the caller with nothing — a failed or empty fetch falls
 * back to MODEL_OPTIONS, and ModelSelect's "Custom…" free-text escape hatch covers
 * whatever neither list knows about.
 */
function curatedFor(provider) {
  return (MODEL_OPTIONS[provider] || []).map((id) => ({
    id, label: id, context_window: null, priced: false,
  }));
}

export function useModelOptions({ provider, connectionId } = {}) {
  const [models, setModels] = useState(() => curatedFor(provider));
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState('curated');
  const [nonce, setNonce] = useState(0);
  const { fetchApi } = useAppApi();

  // fetchApi is a fresh closure every render, so it stays out of the dep array (same
  // stance as AgentDetail's own catalog effects); a ref keeps the latest one reachable.
  const fetchRef = useRef(fetchApi);
  fetchRef.current = fetchApi;

  useEffect(() => {
    if (!provider) {
      setModels([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const params = { provider };
    if (connectionId) params.connection_id = connectionId;
    fetchRef.current('agentModels', params)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.models) ? res.models : [];
        setModels(list.length ? list : curatedFor(provider));
        setSource(list.length ? (res.source || 'discovered') : 'curated');
      })
      .catch(() => {
        if (cancelled) return;
        setModels(curatedFor(provider));
        setSource('curated');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [provider, connectionId, nonce]);

  return { models, loading, source, refresh: () => setNonce((n) => n + 1) };
}

export default useModelOptions;
